import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, Trash, Pencil, CalendarBlank, Clock, MapPin, VideoCamera, ArrowsClockwise, ListBullets, GridFour, CaretLeft, CaretRight, Link as LinkIcon } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { fmtIstDate, todayIst } from '../lib/datetime';
import SearchableSelect from '../components/SearchableSelect';
import { makeMeetingUrl } from '../lib/meeting';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUSES = ['Scheduled', 'Confirmed', 'Done', 'Cancelled', 'No-show'];
const STATUS_COLOR = {
  Scheduled: { bg: '#dbeafe', color: '#1e40af' },
  Confirmed: { bg: '#dcfce7', color: '#15803d' },
  Done:      { bg: '#f3e8ff', color: '#6b21a8' },
  Cancelled: { bg: '#fee2e2', color: '#b91c1c' },
  'No-show': { bg: '#fef3c7', color: '#92400e' },
};

const today = todayIst;
// Local-date YYYY-MM-DD (avoids UTC shift that breaks calendar in IST)
const toLocalISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const addDays = (yyyy_mm_dd, n) => {
  const [y, m, dd] = yyyy_mm_dd.split('-').map(Number);
  const d = new Date(y, m - 1, dd); d.setDate(d.getDate() + n);
  return toLocalISO(d);
};

const empty = () => ({
  title: '', date: today(), start_time: '10:00', end_time: '11:00',
  location: '', meeting_link: '',
  client_id: '', client_kind: '', client_name: '', contact_person: '',
  team_members: [], notes: '', status: 'Scheduled', reminder_minutes: 30,
});

const AppointmentsPage = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [leads, setLeads] = useState([]);
  const [clients, setClients] = useState([]);
  const [viewMode, setViewMode] = useState('calendar'); // 'list' | 'calendar'
  // Calendar month being viewed (1st of month, ISO)
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date(today());
    return toLocalISO(new Date(d.getFullYear(), d.getMonth(), 1));
  });
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(addDays(today(), 30));
  const [filterEmp, setFilterEmp] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [deptFilter, setDeptFilter] = useState('');
  // When user clicks a day in the calendar grid, filter the list to that day
  const [activeDay, setActiveDay] = useState('');

  const isAdmin = user?.role === 'admin';
  const myId = user?.id || user?._id || '';

  // When calendar month changes, snap the from/to to the full month
  useEffect(() => {
    if (viewMode !== 'calendar') return;
    const base = new Date(calMonth);
    const last = new Date(base.getFullYear(), base.getMonth() + 1, 0);
    setFromDate(calMonth);
    setToDate(toLocalISO(last));
  }, [calMonth, viewMode]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { from_date: fromDate, to_date: toDate };
      if (filterEmp) params.team_member = filterEmp;
      const [aRes, eRes, dRes, lRes, cRes] = await Promise.all([
        axios.get(`${API_URL}/api/appointments`, { params, withCredentials: true }),
        axios.get(`${API_URL}/api/employees`, { withCredentials: true }),
        axios.get(`${API_URL}/api/departments`, { withCredentials: true }),
        axios.get(`${API_URL}/api/leads`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/clients`, { withCredentials: true }),
      ]);
      setItems(aRes.data || []);
      setEmployees((eRes.data || []).filter((e) => e.is_active !== false));
      setDepartments(dRes.data || []);
      setLeads(lRes.data || []);
      setClients(cRes.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fromDate, toDate, filterEmp]);

  const startNew = () => { setForm(empty()); setEditing({ id: null }); };
  const startEdit = (a) => { setForm({ ...empty(), ...a }); setEditing(a); };
  const cancel = () => { setEditing(null); setForm(empty()); };

  const save = async () => {
    if (!form.title.trim() || !form.date || !form.start_time) {
      alert('Title, date, start time required'); return;
    }
    try {
      if (editing?.id) await axios.put(`${API_URL}/api/appointments/${editing.id}`, form, { withCredentials: true });
      else await axios.post(`${API_URL}/api/appointments`, form, { withCredentials: true });
      cancel(); load();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this appointment?')) return;
    await axios.delete(`${API_URL}/api/appointments/${id}`, { withCredentials: true });
    load();
  };

  const toggleTeam = (eid) => {
    const exists = form.team_members.includes(eid);
    setForm({
      ...form,
      team_members: exists ? form.team_members.filter((x) => x !== eid) : [...form.team_members, eid],
    });
  };

  const filteredEmployees = useMemo(() => {
    if (!deptFilter) return employees;
    return employees.filter((e) => e.department_id === deptFilter);
  }, [employees, deptFilter]);

  // Group by date
  const grouped = useMemo(() => {
    const m = {};
    items.forEach((a) => { (m[a.date] = m[a.date] || []).push(a); });
    return Object.entries(m).sort((a, b) => a[0].localeCompare(b[0]));
  }, [items]);

  return (
    <div data-testid="appointments-page" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--dx-text)' }}>
            <CalendarBlank size={22} className="inline mr-1.5 mb-1" /> Appointments & Meetings
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--dx-text2)' }}>
            {items.length} appointment{items.length !== 1 ? 's' : ''} in selected range
          </p>
        </div>
        <Button onClick={startNew} data-testid="new-appt-btn">
          <Plus size={14} className="mr-1.5" /> New Appointment
        </Button>
      </div>

      <Card className="p-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="filter-from" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="filter-to" />
          </div>
          <div>
            <Label className="text-xs">Team Member</Label>
            <select value={filterEmp} onChange={(e) => setFilterEmp(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm bg-white" data-testid="filter-team">
              <option value="">All</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.name} ({e.emp_id})</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <Button variant="outline" onClick={load} className="w-full">
              <ArrowsClockwise size={14} className="mr-1.5" /> Refresh
            </Button>
          </div>
        </div>
      </Card>

      {/* View toggle */}
      <Card className="p-2 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          {viewMode === 'calendar' && (
            <>
              <button
                onClick={() => {
                  const d = new Date(calMonth);
                  d.setMonth(d.getMonth() - 1);
                  setCalMonth(toLocalISO(d));
                  setActiveDay('');
                }}
                className="px-2 py-1 hover:bg-gray-100 rounded"
                data-testid="cal-prev-month"
              >
                <CaretLeft size={14} />
              </button>
              <div className="text-sm font-bold px-2" style={{ color: 'var(--dx-text)' }} data-testid="cal-month-label">
                {new Date(calMonth).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
              </div>
              <button
                onClick={() => {
                  const d = new Date(calMonth);
                  d.setMonth(d.getMonth() + 1);
                  setCalMonth(toLocalISO(d));
                  setActiveDay('');
                }}
                className="px-2 py-1 hover:bg-gray-100 rounded"
                data-testid="cal-next-month"
              >
                <CaretRight size={14} />
              </button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const t = new Date(today());
                  setCalMonth(toLocalISO(new Date(t.getFullYear(), t.getMonth(), 1)));
                  setActiveDay(today());
                }}
                data-testid="cal-today-btn"
              >
                Today
              </Button>
            </>
          )}
        </div>
        <div className="flex items-center gap-1 rounded border bg-white p-0.5">
          <button
            onClick={() => { setViewMode('calendar'); setActiveDay(''); }}
            className={`px-2.5 py-1 text-xs font-bold rounded ${viewMode === 'calendar' ? 'bg-emerald-600 text-white' : 'text-gray-600'}`}
            data-testid="view-calendar"
          >
            <GridFour size={12} className="inline mr-1" /> Calendar
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-2.5 py-1 text-xs font-bold rounded ${viewMode === 'list' ? 'bg-emerald-600 text-white' : 'text-gray-600'}`}
            data-testid="view-list"
          >
            <ListBullets size={12} className="inline mr-1" /> List
          </button>
        </div>
      </Card>

      {/* Calendar (month grid) */}
      {viewMode === 'calendar' && (
        <Card className="p-3" data-testid="cal-grid">
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: 'var(--dx-text2)' }}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <div key={d}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {(() => {
              const base = new Date(calMonth);
              const first = new Date(base.getFullYear(), base.getMonth(), 1);
              const last = new Date(base.getFullYear(), base.getMonth() + 1, 0);
              const todayStr = today();
              const cells = [];
              for (let i = 0; i < first.getDay(); i++) cells.push(null);
              for (let d = 1; d <= last.getDate(); d++) {
                cells.push(toLocalISO(new Date(base.getFullYear(), base.getMonth(), d)));
              }
              return cells.map((iso, i) => {
                if (!iso) return <div key={i} />;
                const dayAppts = items.filter((a) => a.date === iso);
                const isToday = iso === todayStr;
                const isActive = activeDay === iso;
                return (
                  <button
                    key={i}
                    onClick={() => setActiveDay(activeDay === iso ? '' : iso)}
                    className={`text-left rounded p-1.5 min-h-[64px] border transition-colors ${isActive ? 'border-emerald-500 bg-emerald-50' : isToday ? 'border-amber-400 bg-amber-50' : 'border-slate-200 hover:bg-slate-50'}`}
                    data-testid={`cal-day-${iso}`}
                  >
                    <div className={`text-[11px] font-bold ${isToday ? 'text-amber-700' : 'text-slate-700'}`}>{Number(iso.slice(-2))}</div>
                    <div className="mt-0.5 space-y-0.5">
                      {dayAppts.slice(0, 3).map((a) => {
                        const sc = STATUS_COLOR[a.status] || STATUS_COLOR.Scheduled;
                        return (
                          <div key={a.id} className="text-[9px] truncate rounded px-1" style={{ background: sc.bg, color: sc.color }}>
                            {a.start_time} {a.title}
                          </div>
                        );
                      })}
                      {dayAppts.length > 3 && (
                        <div className="text-[9px] text-gray-500">+{dayAppts.length - 3} more</div>
                      )}
                    </div>
                  </button>
                );
              });
            })()}
          </div>
        </Card>
      )}

      {/* Day list (also shown under calendar; when activeDay set, only that day) */}
      {loading ? (
        <Card className="p-8 text-center text-sm">Loading…</Card>
      ) : (() => {
        const visible = activeDay ? grouped.filter(([d]) => d === activeDay) : grouped;
        return visible.length === 0 ? (
          <Card className="p-8 text-center text-sm" style={{ color: 'var(--dx-text3)' }}>
            {activeDay ? `No appointments on ${activeDay}.` : 'No appointments in this range.'}
          </Card>
        ) : visible.map(([d, list]) => (
        <Card key={d} className="p-0 overflow-hidden">
          <div className="px-4 py-2 text-sm font-bold" style={{ background: 'var(--dx-bg)' }}>
            {fmtIstDate(d)}
            <span className="text-xs font-normal ml-2 text-gray-500">({list.length})</span>
          </div>
          <div className="divide-y">
            {list.sort((a, b) => a.start_time.localeCompare(b.start_time)).map((a) => {
              const sc = STATUS_COLOR[a.status] || STATUS_COLOR.Scheduled;
              return (
                <div key={a.id} className="p-3 hover:bg-gray-50 flex items-start gap-3" data-testid={`appt-${a.id}`}>
                  <div className="flex-shrink-0 text-right" style={{ minWidth: 72 }}>
                    <div className="text-sm font-bold font-mono" style={{ color: 'var(--dx-text)' }}>{a.start_time}</div>
                    <div className="text-[10px] text-gray-500">{a.end_time}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <strong className="text-sm">{a.title}</strong>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: sc.bg, color: sc.color }}>{a.status.toUpperCase()}</span>
                      {a.client_name && (
                        <span className="text-[11px] px-1.5 py-0.5 rounded" style={{ background: '#f1f5f9', color: '#334155' }}>
                          {a.client_kind === 'lead' ? '🎯' : '🤝'} {a.client_name}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-gray-600 mt-1 flex items-center gap-2 flex-wrap">
                      {a.contact_person && <span>👤 <strong>{a.contact_person}</strong></span>}
                      {a.location && <span><MapPin size={11} className="inline mr-0.5" />{a.location}</span>}
                      {a.meeting_link && <a href={a.meeting_link} target="_blank" rel="noreferrer" className="text-blue-600 underline"><VideoCamera size={11} className="inline mr-0.5" />Meeting link</a>}
                      {a.team_member_names?.length > 0 && (
                        <span>👥 {a.team_member_names.join(', ')}</span>
                      )}
                    </div>
                    {a.notes && <div className="text-[11px] text-gray-500 mt-1 italic">{a.notes}</div>}
                  </div>
                  <div className="flex gap-1">
                    {(isAdmin || !a.created_by_user_id || a.created_by_user_id === myId) && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => startEdit(a)} data-testid={`edit-${a.id}`}>
                          <Pencil size={12} />
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(a.id)} data-testid={`del-${a.id}`}>
                          <Trash size={12} />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      ));
      })()}

      {/* Form modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={cancel}>
          <Card className="w-full max-w-2xl bg-white p-5 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">{editing.id ? 'Edit Appointment' : 'New Appointment'}</h2>
              <button onClick={cancel} className="text-2xl leading-none px-2">×</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Title *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Client Demo / Sales Call / Project Kickoff" data-testid="appt-title-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Date *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="appt-date-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm bg-white">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Start *</Label>
                <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">End</Label>
                <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Location</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Office / Onsite / Online" />
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Meeting Link</Label>
                  <button
                    type="button"
                    onClick={() => {
                      const url = makeMeetingUrl({ prefix: 'appt', contextId: editing?.id || form.title || 'new', mode: 'video' });
                      setForm({ ...form, meeting_link: url });
                      toast.success('Meeting link generated — will be shared with the client');
                    }}
                    className="text-[10px] font-bold text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1"
                    data-testid="generate-meeting-link-btn"
                    title="Generate a free Jitsi meeting room"
                  >
                    <LinkIcon size={10} weight="bold" /> Generate Link
                  </button>
                </div>
                <Input value={form.meeting_link} onChange={(e) => setForm({ ...form, meeting_link: e.target.value })} placeholder="https://meet.jit.si/… or paste any link" data-testid="appt-meeting-link-input" />
              </div>

              {/* Client picker — searchable to handle long lead/client lists */}
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Client (Lead or existing Client)</Label>
                <SearchableSelect
                  testId="appt-client-select"
                  placeholder="— internal meeting (no client) —"
                  value={form.client_id ? `${form.client_kind}:${form.client_id}` : ''}
                  options={[
                    ...leads.map((l) => ({
                      value: `lead:${l.id}`,
                      label: `🎯 ${l.name}${l.company ? ` — ${l.company}` : ''}`,
                      sublabel: 'Lead (CRM)',
                      search: `${l.name || ''} ${l.company || ''} ${l.email || ''} ${l.phone || ''} lead`,
                    })),
                    ...clients.map((c) => ({
                      value: `client:${c.id}`,
                      label: `🤝 ${c.first_name || ''} ${c.last_name || ''}${c.company_name ? ` — ${c.company_name}` : ''}`.trim(),
                      sublabel: 'Existing Client',
                      search: `${c.first_name || ''} ${c.last_name || ''} ${c.company_name || ''} ${c.email || ''} ${c.phone || ''} client`,
                    })),
                  ]}
                  onChange={(v) => {
                    if (!v) {
                      setForm({ ...form, client_id: '', client_kind: '', client_name: '', contact_person: '' });
                      return;
                    }
                    const [kind, id] = v.split(':');
                    let defaultContact = '';
                    let snapshotName = '';
                    if (kind === 'lead') {
                      const l = leads.find((x) => x.id === id);
                      defaultContact = l?.name || '';
                      snapshotName = l?.name || '';
                    } else {
                      const c = clients.find((x) => x.id === id);
                      defaultContact = `${c?.first_name || ''} ${c?.last_name || ''}`.trim();
                      snapshotName = defaultContact || c?.company_name || '';
                    }
                    setForm({
                      ...form,
                      client_id: id,
                      client_kind: kind,
                      client_name: snapshotName,
                      contact_person: defaultContact,
                    });
                  }}
                />
              </div>

              {/* Contact person (free text — auto-filled from client default, editable) */}
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Contact Person {form.client_id && <span className="text-[10px] text-gray-500 font-normal">(auto-filled from client; edit if a different person will attend)</span>}</Label>
                <Input
                  value={form.contact_person || ''}
                  onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                  placeholder="Who from the other side is attending?"
                  data-testid="appt-contact-person-input"
                />
              </div>

              {/* Team members */}
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Team Members ({form.team_members.length} selected)</Label>
                <select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm bg-white mb-2">
                  <option value="">All departments</option>
                  {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                <div className="max-h-[180px] overflow-y-auto border rounded p-2 space-y-1">
                  {filteredEmployees.map((e) => {
                    const checked = form.team_members.includes(e.id);
                    return (
                      <label key={e.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer hover:bg-gray-50 px-1 rounded">
                        <input type="checkbox" checked={checked} onChange={() => toggleTeam(e.id)} data-testid={`team-${e.emp_id}`} />
                        <span className="font-medium">{e.name}</span>
                        <span className="text-[10px] font-mono text-gray-500">{e.emp_id}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Notes</Label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm min-h-[60px]" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Reminder (min before)</Label>
                <Input type="number" value={form.reminder_minutes} onChange={(e) => setForm({ ...form, reminder_minutes: parseInt(e.target.value) || 30 })} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={cancel}>Cancel</Button>
              <Button onClick={save} data-testid="appt-save-btn">{editing.id ? 'Update' : 'Create'}</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AppointmentsPage;
